import { get, post, put, type ReqConfig } from '../request';
import { toPage, type Page, type PageQuery } from './types';

export interface ChatSession {
  id: number;
  /** 会话编号（后端 C+日期+流水，唯一；Tab 兜底标题 / tooltip） */
  session_no?: string;
  visitor_name?: string;
  /** 承办坐席（agent 表 id）；空 = 未分配（机器人接待中 / 公共排队池） */
  agent_id?: number | null;
  /** 访客手机号（身份核验通过 / CRM 匹配后带出；Tab 命名用尾号） */
  phone?: string;
  /** 关联 CRM 客户（身份识别后置位；右栏语境用） */
  customer_id?: number | null;
  /** 进入排队时间（排队块等待时长） */
  entered_queue_at?: string;
  status?: string; // queued|robot|human|closed
  channel?: string; // h5|wechat|api
  /**
   * 访客通道是否在线（后端按 chat:ws / chat:voice presence 键实时判定）。
   * false = 访客已刷新离开/关闭页面/断网（含 15s 宽限），会话仍存活但坐席侧应标注「已离线」。
   */
  visitor_online?: boolean;
  agent_name?: string;
  skill_group_name?: string;
  satisfaction_score?: number | null;
  message_count?: number;
  created_at?: string;
  closed_at?: string;
  /**
   * **前端派生标记，不是后端字段**（后端响应里没有它）：该会话是在访客提交满意度评价后关闭的。
   * 工作台收到 `chat_closed(reason=satisfaction)` 时就地打标（useChatConversation），
   * 已评价会话并入 Tab 时也补标（workbench/index.tsx）；刷新后由服务端列表重新推导。
   * 显式声明（而不是靠 `as any` 或索引签名绕过）：那两种写法会同时放开字段名与类型，
   * 字段改名或写入端漏传都不会有编译错误，读写两侧就此静默失联。
   */
  surveyedClosed?: boolean;
  [k: string]: any;
}

export interface ChatMessage {
  id?: number;
  session_id?: number;
  sender_type: string; // visitor|agent|robot|system
  content: string;
  agent_name?: string;
  type?: 'text' | 'image' | 'file';
  content_url?: string;
  created_at?: string;
  [k: string]: any;
}

// ---------- 在线客服增强 ----------

/** 坐席上传富媒体附件（自动经 chat:agent_send 下发访客） */
export function uploadChatAttachment(sessionId: number, file: File): Promise<{ content_url: string; type: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return post(`/chat/sessions/${sessionId}/attachments`, fd, { timeout: 60000 });
}

/** 机器人阶段小结（认领时随路） */
export function getBotSummary(sessionId: number): Promise<{ summary: string }> {
  return get(`/chat/sessions/${sessionId}/bot-summary`);
}

/** 接待量自定 */
export function setMaxSessions(maxSessions?: number) {
  return put(`/chat/me/max-sessions`, { max_sessions: maxSessions });
}

/**
 * GET /chat/sessions（后端认 status / phone / agent_id / dateFrom / dateTo / handled 筛选）。
 * - phone 为关联手机号，可只填一段号码；
 * - agent_id / dateFrom / dateTo / handled 给「服务记录」在线页签用：按承办坐席、按近 7 天（含两端）、
 *   只看坐席实际接待过的会话（机器人全程接待的不算服务）。后端未部署这组参数时会被忽略，不会报错。
 */
export async function listSessions(
  q: PageQuery & {
    status?: string;
    phone?: string;
    agent_id?: number;
    dateFrom?: string;
    dateTo?: string;
    handled?: boolean;
  }
): Promise<Page<ChatSession>> {
  return toPage<ChatSession>(await get('/chat/sessions', { ...q, handled: q.handled ? '1' : undefined }));
}

/** GET /chat/sessions/:id（会话详情，附 agent_name/message_count；VOC 证据深链） */
export async function getSession(id: number): Promise<ChatSession> {
  return get(`/chat/sessions/${id}`);
}

/**
 * GET /chat/sessions/:id/messages
 * cfg 供「内容面可能读不到」的调用点传 `{ silent: true }`：在线会话内容只对
 * 「本人承办 / admin / 正在关注该会话」放行，其余读取后端回 404——这是调用方的预期分支
 * （工作台据此转「需先关注」空态），不该再由拦截器弹一次全局错误；其余错误仍走拦截器提示。
 */
export async function listMessages(sessionId: number, q?: PageQuery, cfg?: ReqConfig): Promise<Page<ChatMessage>> {
  return toPage<ChatMessage>(
    await get(`/chat/sessions/${sessionId}/messages`, { page: 1, pageSize: 200, ...(q || {}) }, cfg)
  );
}

/** 会话流程轨迹事件（会话记录「流程轨迹」标签 / 工作台实时时间线） */
export interface FlowTrailEvent {
  event: string;
  node_key: string;
  node_name?: string; // 节点中文名（biz_flow_node.name，流程踪迹中文展示）
  flow_name?: string; // 事件所属流程中文名（payload.flow 对应）
  payload?: Record<string, any>;
  ts: string | number;
}

export interface ChatFlowTrail {
  flow_id: number;
  flow_code: string;
  flow_name?: string; // 流程中文名（biz_flow.name）
  state: string;
  events: FlowTrailEvent[];
}

/**
 * GET /chat/sessions/:id/flow-trail：会话流程轨迹（process_event_log 时间线）
 * cfg 供「内容面可能读不到」的调用点传 `{ silent: true }`：流程轨迹属受限内容，
 * 他人承办的会话未关注时后端回 404——工作台该读失败只表现为「轨迹暂缺」，不弹全局错误。
 */
export async function getFlowTrail(sessionId: number, cfg?: ReqConfig): Promise<ChatFlowTrail> {
  return get(`/chat/sessions/${sessionId}/flow-trail`, undefined, cfg);
}

/** GET /chat/sessions/:id/flow-context：挂起流程摘要自愈拉取（§6.3）；无挂起 flow 为 null */
export async function getFlowContext(sessionId: number): Promise<{ flow: Record<string, any> | null }> {
  return get(`/chat/sessions/${sessionId}/flow-context`);
}

/** POST /chat/sessions/:id/claim */
export function claimSession(id: number) {
  return post(`/chat/sessions/${id}/claim`);
}

/** POST /chat/sessions/:id/close */
export function closeSession(id: number) {
  return post(`/chat/sessions/${id}/close`);
}

/** POST /chat/sessions/:id/summary {summary}：会话小结 */
export function summarySession(id: number, summary: string) {
  return post(`/chat/sessions/${id}/summary`, { summary });
}

/**
 * POST /chat/sessions/:id/transfer {agent_id?, skill_group_id?}：
 * agentId 非空 = 转指定坐席；skillGroupId 非空 = 转技能组排队（组内自动分配）；均空 = 转回排队。
 */
export function transferSession(id: number, agentId?: number, skillGroupId?: number) {
  const body: Record<string, number> = {};
  if (agentId) body.agent_id = agentId;
  if (skillGroupId) body.skill_group_id = skillGroupId;
  return post(`/chat/sessions/${id}/transfer`, body);
}
